import type { IDownloadProgressEvent } from "./chat";

// NOTE: NSAI-4326 — one AI-generated file as `GET /ai-generated-files` lists
// it. Shared by web (`/ai-generated-files` page) and mobile (AI Generated Files
// screen) so both clients read the same contract.
export interface IGeneratedFile {
  uuid: string;
  name: string;
  type: string;
  fileSize: number | null;
  source: string;
  isImage: boolean;
  messageUuid: string | null;
  chatUuid: string | null;
  // NOTE: The producing automation run — exactly one is set for automation
  // files, all null for chat files. Drives the Source label (Agent/Webhook/Event Rule).
  cronRunUuid: string | null;
  webhookRunUuid: string | null;
  hookRunUuid: string | null;
  // NOTE: Parent automation entity uuid for the /agents?runs= deep-link.
  automationUuid: string | null;
  // NOTE: NSAI-6501 — on the caller's own files: whether anyone has access
  // through a share. On a file shared with the caller ("shared" scope): always
  // true, `sharedBy` names who shared it, and every chat / run link above is
  // null (they point into the owner's workspace).
  isShared: boolean;
  // NOTE: NSAI-6688 / NSAI-6697 — on the caller's own files: whether any share
  // row exists, including ones that no longer grant access (a person who left
  // the organization, a deleted project). Keeps the Share action reachable for
  // an owner without chat_share:write so they can remove them. Always false on
  // a file shared with the caller.
  hasShares: boolean;
  sharedBy: IGeneratedFileSharedBy | null;
  createdAt: string;
}

export interface IGeneratedFileSharedBy {
  name: string | null;
  email: string | null;
  // NOTE: NSAI-7736 — the IdP login name of a sharer with no email.
  username?: string | null;
}

export interface IGeneratedFilesResponse {
  data: IGeneratedFile[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type TGeneratedFileType =
  | "all"
  | "image"
  | "document"
  | "presentation"
  | "page";

// NOTE: "automation" is a defensive fallback — real listings always carry a
// chatUuid or exactly one run FK.
export type TGeneratedFileSource =
  | "chat"
  | "agent"
  | "webhook"
  | "eventRule"
  | "automation";

// NOTE: NSAI-6501 — "mine" is the caller's own files (the default), "shared"
// the files other people shared with them.
export type TGeneratedFilesScope = "mine" | "shared";

export interface IGetGeneratedFilesParams {
  scope?: TGeneratedFilesScope;
  search?: string;
  // NOTE: NSAI-5312 — one chat's files only, for the chat menu's "Download
  // generated files" dialog. The server still scopes it to the caller's chats.
  chatUuid?: string;
  fileType?: TGeneratedFileType;
  // NOTE: ISO 8601 timestamps. Caller is responsible for converting picked
  // dates to start-of-day / end-of-day (e.g. via date-fns) so the range is
  // inclusive at both ends from the user's perspective.
  fromDate?: string;
  toDate?: string;
  page?: number;
  limit?: number;
}

// NOTE: The wire shape of the query string — every optional filter already
// dropped when it carries no information (empty search, `all` type).
export interface IGeneratedFilesQueryParams {
  page: number;
  limit: number;
  scope?: Exclude<TGeneratedFilesScope, "mine">;
  search?: string;
  chatUuid?: string;
  fileType?: Exclude<TGeneratedFileType, "all">;
  fromDate?: string;
  toDate?: string;
}

export interface IGeneratedFileTypeOption {
  value: TGeneratedFileType;
  labelKey:
    | "generated_files.filter_all"
    | "generated_files.filter_images"
    | "generated_files.filter_documents"
    | "generated_files.filter_presentations"
    | "generated_files.filter_pages";
}

// NOTE: NSAI-5312 — what one archive download names: files picked on the AI
// Generated Files page, every generated file of a chat, or every file an
// automation run produced. Mirrors the server's EGeneratedFilesArchiveScope.
export enum EGeneratedFilesArchiveScope {
  SELECTION = "selection",
  CHAT = "chat",
  RUN = "run",
}

export interface IGeneratedFilesArchiveSelectionRequest {
  scope: EGeneratedFilesArchiveScope.SELECTION;
  fileUuids: string[];
}

export interface IGeneratedFilesArchiveChatRequest {
  scope: EGeneratedFilesArchiveScope.CHAT;
  chatUuid: string;
}

export interface IGeneratedFilesArchiveRunRequest {
  scope: EGeneratedFilesArchiveScope.RUN;
  runUuid: string;
}

export type TGeneratedFilesArchiveRequest =
  | IGeneratedFilesArchiveSelectionRequest
  | IGeneratedFilesArchiveChatRequest
  | IGeneratedFilesArchiveRunRequest;

// NOTE: The wire shape of the archive query — exactly one field is set, and a
// selection travels as one comma-separated value (see
// buildGeneratedFilesArchiveQueryParams for why).
export interface IGeneratedFilesArchiveQueryParams {
  fileUuids?: string;
  chatUuid?: string;
  runUuid?: string;
}

export interface IDownloadGeneratedFilesArchiveParams {
  request: TGeneratedFilesArchiveRequest;
  onProgress?: (event: IDownloadProgressEvent) => void;
  signal?: AbortSignal;
}

export interface IBuildGeneratedFilesArchiveFileNameProps {
  date: Date;
}

export interface IResolveGeneratedFilesArchiveErrorKeyProps {
  status: number | undefined;
  // NOTE: A 404 means different things per scope: a selected file that is
  // gone, versus a chat or run with nothing to archive.
  scope: EGeneratedFilesArchiveScope;
}

// NOTE: i18n keys (common namespace) a failed archive download is worded with.
// A union of literal keys, like TAttachmentDownloadErrorKey, so the typed `t()`
// accepts every value.
export type TGeneratedFilesArchiveErrorKey =
  | "generated_files.archive.errors.access_denied"
  | "generated_files.archive.errors.unavailable"
  | "generated_files.archive.errors.no_files"
  | "generated_files.archive.errors.too_large"
  | "generated_files.archive.errors.throttled"
  | "generated_files.archive.errors.download_failed";

// NOTE: NSAI-5312 — the state of a "select the whole page" box. Both
// platforms' checkbox primitives take exactly this union (Radix on web, the
// app's own on mobile), so it is kept as their shared shape.
export type TGeneratedFilesSelectionCheckedState = boolean | "indeterminate";

// NOTE: NSAI-5312 — the files ticked for download, keyed by uuid. The file
// itself is kept, not only its uuid: a selection can span pages, and a lone
// ticked file is saved under its own name, which a page the user has since
// left no longer holds.
export type TGeneratedFilesSelection = ReadonlyMap<string, IGeneratedFile>;

export interface IToggleGeneratedFileSelectionProps {
  selectedFiles: TGeneratedFilesSelection;
  file: IGeneratedFile;
}

export interface ISetGeneratedFilesPageSelectionProps {
  selectedFiles: TGeneratedFilesSelection;
  pageFiles: readonly IGeneratedFile[];
  isSelected: boolean;
}

export interface IResolveGeneratedFilesPageSelectionStateProps {
  selectedFiles: TGeneratedFilesSelection;
  pageFiles: readonly IGeneratedFile[];
}

export interface IResolvePickedGeneratedFilesProps {
  files: readonly IGeneratedFile[];
  // NOTE: `null` until the user changes a tick — everything listed is picked.
  pickedFiles: TGeneratedFilesSelection | null;
}

// NOTE: NSAI-5312 — what a chat's "Download generated files" picker shows: the
// picked files in listing order, the same files keyed for the row ticks, and
// the select-all box.
export interface IGeneratedFilesPickerState {
  selectedFiles: readonly IGeneratedFile[];
  selection: TGeneratedFilesSelection;
  checkedState: TGeneratedFilesSelectionCheckedState;
}

// NOTE: NSAI-5312 — the files ticked on the AI Generated Files page,
// remembered together with the filter state they were ticked under.
export interface IGeneratedFilesSelectionState {
  filterKey: string;
  files: TGeneratedFilesSelection;
}

export interface IStartGeneratedFilesSelectionProps {
  filterKey: string;
}

export interface IReadGeneratedFilesSelectionProps {
  state: IGeneratedFilesSelectionState;
  filterKey: string;
}

export interface IToggleGeneratedFileInSelectionProps {
  state: IGeneratedFilesSelectionState;
  file: IGeneratedFile;
}

export interface ISetGeneratedFilesPageInSelectionProps {
  state: IGeneratedFilesSelectionState;
  pageFiles: readonly IGeneratedFile[];
  isSelected: boolean;
}

// NOTE: NSAI-5312 — how the picked files travel: one file as itself (a ZIP of
// one file only adds an unpacking step), two or more as one archive of
// exactly those files.
export enum EGeneratedFilesDownloadKind {
  FILE = "file",
  ARCHIVE = "archive",
}

export interface IGeneratedFilesSingleFileDownload {
  kind: EGeneratedFilesDownloadKind.FILE;
  file: IGeneratedFile;
}

export interface IGeneratedFilesArchiveDownload {
  kind: EGeneratedFilesDownloadKind.ARCHIVE;
  request: IGeneratedFilesArchiveSelectionRequest;
}

export type TGeneratedFilesDownload =
  | IGeneratedFilesSingleFileDownload
  | IGeneratedFilesArchiveDownload;

export interface IResolveGeneratedFilesDownloadProps {
  files: readonly IGeneratedFile[];
}

export interface IIsGeneratedFilesArchiveDownloadProps {
  selectedCount: number;
}

export interface IResolveGeneratedFilesDownloadLabelKeyProps {
  selectedCount: number;
}

// NOTE: i18n keys (common namespace) the download button reads with: "Download"
// for none or one file, "Download as ZIP" once several are picked.
export type TGeneratedFilesDownloadLabelKey =
  | "generated_files.download"
  | "generated_files.archive.download";

// NOTE: NSAI-6501 — sharing an AI-generated file with teammates or whole
// projects (view and download only), as `/ai-generated-files/:fileUuid/…`
// returns it.
export type TGeneratedFileShareTargetType = "user" | "project";

export interface IGeneratedFileShareUser {
  uuid: string;
  name: string | null;
  email: string | null;
  // NOTE: NSAI-7736 — the IdP login name of a recipient with no email.
  username?: string | null;
}

export interface IGeneratedFileShareProject {
  uuid: string;
  name: string;
}

export interface IGeneratedFileShare {
  uuid: string;
  targetType: TGeneratedFileShareTargetType;
  // NOTE: Exactly one of the two is set, matching `targetType`.
  user: IGeneratedFileShareUser | null;
  project: IGeneratedFileShareProject | null;
  // NOTE: NSAI-6697 — false once the target can no longer open the file: the
  // person left the organization or the project was deleted. Still listed, as
  // "No access", so the owner can remove it.
  isActive: boolean;
  createdAt: string;
}

export interface IGeneratedFileSharesResponse {
  shares: IGeneratedFileShare[];
}

export interface IGeneratedFileShareProjectsResponse {
  projects: IGeneratedFileShareProject[];
}

export interface IGeneratedFileShareResult {
  // NOTE: The email or project uuid as sent, to pair a result with its chip.
  target: string;
  targetType: TGeneratedFileShareTargetType;
  success: boolean;
  error?: string;
}

export interface IShareGeneratedFileResponse {
  results: IGeneratedFileShareResult[];
  shares: IGeneratedFileShare[];
}

export interface IShareGeneratedFilePayload {
  userEmails?: string[];
  // NOTE: NSAI-7736 — teammates by account, for one with no email (a server
  // that predates the field refuses it).
  userUuids?: string[];
  projectUuids?: string[];
}

export interface IShareGeneratedFileParams {
  fileUuid: string;
  payload: IShareGeneratedFilePayload;
}

export interface IRemoveGeneratedFileSharesParams {
  fileUuid: string;
  shareUuids: string[];
}

// NOTE: NSAI-6501 — the i18n keys of the listing's empty state, one pair per
// reason the list is empty.
export type TGeneratedFilesEmptyTitleKey =
  | "generated_files.empty"
  | "generated_files.empty_filtered"
  | "generated_files.empty_shared";

export type TGeneratedFilesEmptyDescriptionKey =
  | "generated_files.empty_description"
  | "generated_files.empty_filtered_description"
  | "generated_files.empty_shared_description";

export interface IGeneratedFilesEmptyStateKeys {
  titleKey: TGeneratedFilesEmptyTitleKey;
  descriptionKey: TGeneratedFilesEmptyDescriptionKey;
}
